import { boolean } from 'mathjs';
import { YEARFRAC as yearfrac } from 'common/formulas/math.js';
import {
  CleanLiquidationPreferenceParameters,
  LedgerConfig,
  LiquidationAndResults,
  LiquidationPreferenceParameters,
  ReadLiquidationPreference,
} from 'pages/CapTable/cap-table/utilities/readLiquidationPreferenceConfig.props';
import { gridShortDate } from 'utilities';
import { BASIS_3_VALUE } from '../components/liquidation-preference-ledger/liq-pref-ledger-table-config/constants';

function calculateLiquidationAndDividends(
  parameters: CleanLiquidationPreferenceParameters,
  exitDate: string,
  issueDate: string,
  issuePrice: number,
  shares: number
): LiquidationAndResults {
  const updatedParameters = {
    ...parameters,
    liquidation: 0,
    accrued_dividends: 0,
  };
  updatedParameters.liquidation = updatedParameters.liquidation_preference * shares * issuePrice;

  if (!parameters.dividend_rate) {
    updatedParameters.accrued_dividends = 0;
    return updatedParameters;
  }

  const time = Number(yearfrac(issueDate, exitDate, parameters.dividend_yearfrac_basis));
  const dividendRate = parameters.dividend_rate;
  const compoundingPeriod = parameters.compounding_period;

  let dividendsPerShare: number;
  if (parameters.has_compounding && compoundingPeriod) {
    dividendsPerShare = issuePrice * ((1 + dividendRate / compoundingPeriod) ** (time * compoundingPeriod) - 1);
  } else {
    dividendsPerShare = dividendRate * issuePrice * time;
  }

  updatedParameters.accrued_dividends = dividendsPerShare * shares;
  return updatedParameters;
}

function cleanTypesForParameters(parameters: LiquidationPreferenceParameters) {
  return {
    ...parameters,
    dividend_rate: parameters.dividend_rate ? Number(parameters.dividend_rate) : undefined,
    compounding_period: parameters.compounding_period ? Number(parameters.compounding_period) : undefined,
    liquidation_preference: Number(parameters.liquidation_preference),
    has_dividends: boolean(parameters.has_dividends),
    has_compounding: boolean(parameters.has_compounding),
  };
}

function readLiquidationPreferenceConfiguration(
  ledgerConfig: LedgerConfig,
  exitDate: string,
  issueDate: string,
  issuePrice: string,
  shares: string
): ReadLiquidationPreference {
  if (ledgerConfig.threshold_type === 'BY_EXIT_DATE') {
    // select item based on which is in regency as of the exit date
    const sameDateItem = ledgerConfig.items.find(
      item => gridShortDate(item.effective_date) === gridShortDate(exitDate)
    );

    if (sameDateItem) {
      const parameters = cleanTypesForParameters(sameDateItem);
      return {
        liquidationPreference: parameters.liquidation_preference,
        hasDividends: parameters.has_dividends,
        accruedDividends: 0,
        dividendRate: parameters.dividend_rate,
        compoundingPeriod: parameters.compounding_period,
        hasCompounding: parameters.has_compounding,
        yearfracBasis: parameters.dividend_yearfrac_basis,
        multipleOf: parameters.multiple_of,
      };
    }

    const previousItems = ledgerConfig.items
      .filter(item => new Date(item.effective_date as string) < new Date(exitDate))
      .toReversed();

    if (previousItems.length > 0) {
      const parameters = cleanTypesForParameters(previousItems[0]);
      return {
        liquidationPreference: parameters.liquidation_preference,
        hasDividends: parameters.has_dividends,
        accruedDividends: 0,
        dividendRate: parameters.dividend_rate,
        compoundingPeriod: parameters.compounding_period,
        hasCompounding: parameters.has_compounding,
        yearfracBasis: parameters.dividend_yearfrac_basis,
        multipleOf: parameters.multiple_of,
      };
    }
    return {
      liquidationPreference: 0,
      hasDividends: false,
      accruedDividends: 0,
      dividendRate: 0,
      compoundingPeriod: 0,
      hasCompounding: false,
      yearfracBasis: BASIS_3_VALUE,
      multipleOf: 0,
    };
  }
  const criteriaCallback = ledgerConfig.threshold_type === 'GREATER_OF' ? Math.max : Math.min;
  const calculatedLiqPrefPlusDividends = ledgerConfig.items.map(parameters =>
    calculateLiquidationAndDividends(
      cleanTypesForParameters(parameters),
      exitDate,
      issueDate,
      Number(issuePrice),
      Number(shares)
    )
  );

  const chosenParameters = calculatedLiqPrefPlusDividends.reduce(
    (prev, curr) =>
      criteriaCallback(prev.liquidation + prev.accrued_dividends, curr.liquidation + curr.accrued_dividends) ===
      prev.liquidation + prev.accrued_dividends
        ? prev
        : curr,
    { liquidation: 0, accrued_dividends: 0 } as LiquidationAndResults
  );

  return {
    liquidationPreference: chosenParameters.liquidation_preference,
    hasDividends: chosenParameters.has_dividends,
    accruedDividends: chosenParameters.accrued_dividends,
    dividendRate: chosenParameters.dividend_rate,
    compoundingPeriod: chosenParameters.compounding_period,
    hasCompounding: chosenParameters.has_compounding,
    yearfracBasis: chosenParameters.dividend_yearfrac_basis,
    multipleOf: chosenParameters.multiple_of,
  };
}

export default readLiquidationPreferenceConfiguration;
